import { useState, useEffect, useCallback } from 'react';
import type { RevisrTag, TagAssignPayload, DocTagsMap, CreateTagParams } from '../types/knowledgeOverrides';
import {
  getTags,
  searchTags as searchTagsApi,
  createTag as createTagApi,
  assignTags as assignTagsApi,
  removeTags as removeTagsApi,
  getTagsByDocs as getTagsByDocsApi,
} from '../services/knowledgeTagsService';

type UseRevisrLibraryTagsReturn = {
  tags: RevisrTag[];
  isLoading: boolean;
  error: string | null;
  searchTags: (q: string) => Promise<RevisrTag[]>;
  createTag: (params: CreateTagParams) => Promise<RevisrTag>;
  assignTags: (payload: TagAssignPayload) => Promise<void>;
  removeTags: (payload: TagAssignPayload) => Promise<void>;
  getTagsByDocs: (docIds: string[]) => Promise<DocTagsMap>;
  refresh: () => void;
};

export const useRevisrLibraryTags = (): UseRevisrLibraryTagsReturn => {
  const [tags, setTags] = useState<RevisrTag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTags = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getTags();
      setTags(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load tags';
      setError(msg);
      console.error('[useRevisrLibraryTags]', msg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  const searchTags = useCallback(async (q: string): Promise<RevisrTag[]> => {
    return searchTagsApi(q);
  }, []);

  const createTag = useCallback(async (params: CreateTagParams): Promise<RevisrTag> => {
    const tag = await createTagApi(params);
    setTags((prev) => [...prev, tag].sort((a, b) => a.name.localeCompare(b.name)));
    return tag;
  }, []);

  const assignTags = useCallback(async (payload: TagAssignPayload): Promise<void> => {
    await assignTagsApi(payload);
  }, []);

  const removeTags = useCallback(async (payload: TagAssignPayload): Promise<void> => {
    await removeTagsApi(payload);
  }, []);

  const getTagsByDocs = useCallback(async (docIds: string[]): Promise<DocTagsMap> => {
    return getTagsByDocsApi(docIds);
  }, []);

  return {
    tags,
    isLoading,
    error,
    searchTags,
    createTag,
    assignTags,
    removeTags,
    getTagsByDocs,
    refresh: loadTags,
  };
};
